import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Loader2,
  Clock,
  MapPin,
  Briefcase,
  UserCheck,
  CheckCircle2,
  Bookmark,
  Building2,
  Layers,
  Star,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@core/auth/useAuth';
import { applicationService } from '@core/supabase/services/applicationService';
import { matchResultService } from '@core/supabase/services/matchResultService';
import { queryKeys } from '@core/query/queryKeys';
import type { MatchResult } from '../types';
import {
  BAND_LABEL,
  alternateRolesHeading,
  bandOf,
  clientNameFromMatch,
  jobTitleFromMatch,
  otherMatchesForCandidate,
  scoreToStars,
} from '../lib/matchDisplay';
import { MatchCardTopReasons } from './MatchCardTopReasons';
import { WhyMatchPopover } from './WhyMatchPopover';
import { ensureApplicationForMatch } from '../lib/ensureMatchApplication';
import { PrivateStorageAvatar } from '@shared/components/ui/PrivateStorageAvatar';
import { readJdTaxonomyFromJob } from '@modules/jobs/jdStructuredTaxonomy';

export type SmartMatchCardVariant = 'pool' | 'byJob' | 'byCandidate';

interface Props {
  match: MatchResult;
  variant: SmartMatchCardVariant;
  showPreserve?: boolean;
  rank?: number;
  /** When set, card opens detail flow instead of navigating away (e.g. Matches tab + right panel). */
  onSelect?: (match: MatchResult) => void;
  selected?: boolean;
  /** All match rows for this candidate (e.g. Matches tab); used to show alternate role recommendations. */
  topJobs?: MatchResult[];
}

function avatarUrlFor(name: string, candidateId?: string): string {
  const seed = candidateId || name.replace(/\s+/g, '');
  const hash = seed.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return `https://i.pravatar.cc/150?u=${encodeURIComponent(seed + hash)}`;
}

/** Storage photo only; avatar illustration when no path or load fails. */
export function MatchCardCandidateAvatar({
  fullName,
  storagePath,
  avatarFailed,
  onImageError,
  onSignFailed,
  size = 'sm',
  candidateId,
}: {
  fullName: string;
  storagePath: string | null | undefined;
  avatarFailed: boolean;
  onImageError: () => void;
  onSignFailed: () => void;
  /** `lg` = larger initials in fallback (e.g. detail panel). */
  size?: 'sm' | 'lg';
  candidateId?: string;
}) {
  const initials =
    fullName.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';

  const avatarFallback = (
    <img
      src={avatarUrlFor(fullName, candidateId)}
      alt=""
      className="h-full w-full object-cover"
      onError={(e) => {
        e.currentTarget.style.display = 'none';
      }}
    />
  );

  const neutralInitials = (
    <span
      className={`flex h-full w-full items-center justify-center bg-slate-200 font-bold text-slate-700 dark:bg-slate-600 dark:text-slate-100 ${
        size === 'lg' ? 'text-2xl' : 'text-xs'
      }`}
    >
      {initials}
    </span>
  );

  if (storagePath?.trim() && !avatarFailed) {
    return (
      <PrivateStorageAvatar
        path={storagePath}
        className="h-full w-full object-cover"
        fallback={avatarFallback}
        onImageError={onImageError}
        onSignFailed={onSignFailed}
      />
    );
  }

  return avatarFallback;
}

function bandBorderClass(band: string): string {
  switch (band) {
    case 'strong_fit':
      return 'border-emerald-400 dark:border-emerald-600';
    case 'good_fit':
      return 'border-teal-400 dark:border-teal-600';
    case 'partial_fit':
      return 'border-amber-400 dark:border-amber-600';
    default:
      return 'border-[color:var(--vscode-border-primary)]';
  }
}

function bandProgressColor(band: string): string {
  switch (band) {
    case 'strong_fit':
      return '#10b981';
    case 'good_fit':
      return '#14b8a6';
    case 'partial_fit':
      return '#f59e0b';
    default:
      return 'var(--vscode-text-tertiary)';
  }
}

function jdTaxonomyFromMatchJob(job: MatchResult['job']): {
  category?: string;
  seniority?: string;
  industry?: string;
} {
  if (!job) return {};
  const t = readJdTaxonomyFromJob(job);
  return {
    category: t.category ?? undefined,
    seniority: t.seniority ?? undefined,
    industry: t.industry ?? undefined,
  };
}

function bandBadgeCls(band: string): string {
  switch (band) {
    case 'strong_fit':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700';
    case 'good_fit':
      return 'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-900/30 dark:text-teal-300 dark:border-teal-700';
    case 'partial_fit':
      return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700';
    default:
      return 'bg-slate-50 text-slate-500 border-slate-200 dark:bg-slate-800/50 dark:text-slate-400 dark:border-slate-600';
  }
}

export function SmartMatchResultCard({
  match: m,
  variant,
  showPreserve = true,
  rank,
  onSelect,
  selected,
  topJobs = [],
}: Props) {
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [shortlisting, setShortlisting] = useState(false);
  const [shortlisted, setShortlisted] = useState(false);
  const [preserveBusy, setPreserveBusy] = useState(false);
  const [preserved, setPreserved] = useState(m.is_preserved === true);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const [inlineToast, setInlineToast] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    setAvatarFailed(false);
  }, [m.candidate_id, m.candidate?.avatar_url]);

  useEffect(() => {
    if (!inlineToast) return;
    const t = window.setTimeout(() => setInlineToast(null), 4200);
    return () => window.clearTimeout(t);
  }, [inlineToast]);

  useEffect(() => {
    let cancelled = false;
    if (!user?.orgId) return;
    applicationService
      .findByCandidateAndJob(user.orgId, m.candidate_id, m.job_id)
      .then((a) => {
        if (!cancelled && a) setShortlisted(true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user?.orgId, m.candidate_id, m.job_id]);

  const band = bandOf(m.composite_score);
  const { label, color } = BAND_LABEL[band];
  const name = m.candidate?.full_name ?? 'Unknown';
  const client = clientNameFromMatch(m);
  const jobTitle = jobTitleFromMatch(m);
  const recRating = m.candidate?.recruiter_rating;
  const llm = m.llm_confidence;
  const stars = scoreToStars(m.composite_score);
  const jdTax = jdTaxonomyFromMatchJob(m.job);
  const barColor = bandProgressColor(band);
  const alternateRoles =
    variant === 'pool' && topJobs.length > 0 ? otherMatchesForCandidate(m, topJobs).slice(0, 3) : [];

  const handleCardClick = () => {
    if (onSelect) onSelect(m);
    else navigate(`/role-fit/${m.candidate_id}/${m.job_id}`);
  };

  const handleShortlistAndIntro = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user || shortlisting) return;
    setShortlisting(true);
    try {
      const { applicationId, created, skipped } = await ensureApplicationForMatch(
        { orgId: user.orgId, id: user.id },
        m
      );
      void queryClient.invalidateQueries({ queryKey: ['applications'] });
      setShortlisted(true);
      if (skipped || !applicationId) {
        setInlineToast({
          kind: 'ok',
          text: 'Shortlisted. Add them to a collection from the Shortlisted tab.',
        });
        return;
      }
      setInlineToast({
        kind: 'ok',
        text: created ? 'Shortlisted. Opening intro email.' : 'Opening intro email.',
      });
      navigate(
        `/emails/compose?applicationId=${encodeURIComponent(applicationId)}&returnTo=${encodeURIComponent('/smart-match')}`
      );
    } catch (err) {
      setInlineToast({
        kind: 'err',
        text: err instanceof Error ? err.message : 'Could not add to pipeline.',
      });
    } finally {
      setShortlisting(false);
    }
  };

  const togglePreserve = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!orgId || preserveBusy) return;
    setPreserveBusy(true);
    try {
      const next = !preserved;
      await matchResultService.setPreserved(orgId, m.candidate_id, m.job_id, next);
      setPreserved(next);
      void queryClient.invalidateQueries({ queryKey: queryKeys.smartMatch.matchResults(orgId) });
      void queryClient.invalidateQueries({ queryKey: ['smart-match'] });
      setInlineToast({ kind: 'ok', text: next ? 'Match snapshot pinned.' : 'Pin removed.' });
    } catch {
      setInlineToast({
        kind: 'err',
        text: 'Could not update pin. Check access or migration 087.',
      });
    } finally {
      setPreserveBusy(false);
    }
  };

  const selectedRing =
    selected === true
      ? 'ring-2 ring-[color:var(--vscode-primary,#8b80f9)] ring-offset-2 ring-offset-[color:var(--vscode-bg-secondary)]'
      : '';

  return (
    <div
      className={`ggj-match-card ggj-match-card--mobile relative flex flex-col rounded-3xl border-l-[4px] transition-all duration-200 hover:-translate-y-1 hover:shadow-lg cursor-pointer ${bandBorderClass(band)} ${selectedRing}`}
      style={{
        borderLeftWidth: '4px',
        background: 'var(--vscode-bg-elevated)',
      }}
      onClick={handleCardClick}
    >
      {variant === 'pool' ? (
        <>
          {/* Profile-style header (grid / reference layout) */}
          <div
            className="rounded-t-3xl px-4 pb-3 pt-4"
            style={{
              background: 'var(--vscode-bg-elevated)',
            }}
          >
            <div className="flex gap-3">
              <div className="flex shrink-0 flex-col items-center gap-1">
                <div className="relative">
                  <div
                    className="relative flex h-14 w-14 overflow-hidden rounded-full border-2 bg-[color:var(--vscode-bg-secondary)] text-sm font-bold"
                    style={{
                      borderColor: 'color-mix(in srgb, var(--vscode-primary) 40%, var(--vscode-border-primary))',
                    }}
                  >
                    <MatchCardCandidateAvatar
                      fullName={name}
                      storagePath={m.candidate?.avatar_url}
                      avatarFailed={avatarFailed}
                      onImageError={() => setAvatarFailed(true)}
                      onSignFailed={() => setAvatarFailed(true)}
                      candidateId={m.candidate_id}
                    />
                  </div>
                  <span
                    className="absolute bottom-0.5 right-0.5 h-3 w-3 rounded-full border-2 border-white bg-emerald-500 dark:border-slate-900"
                    aria-hidden
                  />
                </div>
                <span
                  className="max-w-[4.5rem] text-center text-[9px] font-bold uppercase leading-tight tracking-wide text-emerald-600 dark:text-emerald-400"
                  title="Candidate is in the active match pool for this role"
                >
                  In pool
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-base font-bold leading-tight" style={{ color: 'var(--vscode-text-primary)' }}>
                  {name}
                </p>
                {m.candidate?.location ? (
                  <p className="mt-1 flex items-center gap-1 truncate text-xs font-medium" style={{ color: 'var(--vscode-text-primary)' }}>
                    <MapPin className="h-3 w-3 shrink-0" />
                    {m.candidate.location}
                  </p>
                ) : (
                  <p className="mt-1 text-xs font-medium italic" style={{ color: 'var(--vscode-text-primary)' }}>
                    Location not set
                  </p>
                )}
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1">
                {rank != null && (
                  <div
                    className="flex items-center gap-2"
                    title={`Rank ${rank}`}
                  >
                    <div
                      className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-[3px] shadow-sm"
                      style={{
                        borderColor: 'var(--vscode-primary, #8b80f9)',
                        background: 'var(--vscode-bg-elevated)',
                        color: 'var(--vscode-primary, #8b80f9)',
                        boxShadow: '0 1px 6px color-mix(in srgb, var(--vscode-primary, #8b80f9) 28%, transparent)',
                      }}
                    >
                      <span className="text-xl font-black leading-none tabular-nums tracking-tight">{rank}</span>
                    </div>
                    <span
                      className="text-[10px] font-bold uppercase leading-tight tracking-wide"
                      style={{ color: 'var(--vscode-text-primary)' }}
                    >
                      Rank
                    </span>
                  </div>
                )}
                {showPreserve && (
                  <button
                    type="button"
                    onClick={togglePreserve}
                    disabled={preserveBusy}
                    title={preserved ? 'Preserved — re-scores will not overwrite' : 'Preserve snapshot'}
                    className={`rounded-full p-1.5 transition-colors ${
                      preserved
                        ? 'bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400'
                        : 'text-[color:var(--vscode-text-tertiary)] hover:bg-[color:var(--vscode-bg-secondary)]'
                    } disabled:opacity-50`}
                  >
                    {preserveBusy ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Bookmark className={`h-3.5 w-3.5 ${preserved ? 'fill-current' : ''}`} />
                    )}
                  </button>
                )}
              </div>
            </div>

            <div
              className="mt-3 grid items-start gap-x-3 gap-y-2"
              style={{ gridTemplateColumns: 'minmax(5.25rem, 6.75rem) minmax(0, 1fr)' }}
            >
              <span
                className="pt-0.5 text-[11px] font-semibold uppercase tracking-wide leading-snug"
                style={{ color: 'var(--vscode-text-primary)' }}
              >
                Position
              </span>
              <div className="min-w-0">
                <p className="truncate text-[15px] font-semibold leading-snug" style={{ color: 'var(--vscode-text-primary)' }}>
                  {jobTitle}
                </p>
                <p className="mt-0.5 flex min-w-0 items-center gap-1 truncate text-xs font-medium" style={{ color: 'var(--vscode-text-primary)' }}>
                  <Building2 className="h-3 w-3 shrink-0" />
                  {client}
                </p>
                {(jdTax.category || jdTax.seniority || jdTax.industry) && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <Layers className="h-3 w-3 shrink-0" style={{ color: 'var(--vscode-text-primary)' }} />
                    {jdTax.category && (
                      <span
                        className="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                        style={{
                          background: 'var(--ggj-card-illus)',
                          color: 'var(--vscode-text-primary)',
                          border: '1px solid color-mix(in srgb, var(--vscode-primary) 30%, var(--vscode-border-primary))',
                        }}
                      >
                        {jdTax.category}
                      </span>
                    )}
                    {jdTax.seniority && (
                      <span
                        className="rounded-full border px-2 py-0.5 text-[10px] font-semibold"
                        style={{
                          background: 'var(--vscode-bg-secondary)',
                          color: 'var(--vscode-text-primary)',
                          borderColor: 'var(--vscode-border-primary)',
                        }}
                      >
                        {jdTax.seniority}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <span
                className="pt-0.5 text-[11px] font-semibold uppercase tracking-wide leading-snug"
                style={{ color: 'var(--vscode-text-primary)' }}
              >
                Match score
              </span>
              <div className="min-w-0">
                <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1">
                  <span className="text-lg font-black tabular-nums" style={{ color: 'var(--vscode-text-primary)' }}>
                    {m.composite_score}
                    <span className="text-xs font-semibold">/100</span>
                  </span>
                  <div className="flex items-center gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        className={`h-4 w-4 sm:h-[1.125rem] sm:w-[1.125rem] shrink-0 ${i < stars ? 'fill-amber-400 text-amber-400' : 'text-slate-300 dark:text-slate-500'}`}
                        aria-hidden
                      />
                    ))}
                  </div>
                </div>
                <div
                  className="mt-1.5 h-2 overflow-hidden"
                  style={{ background: 'color-mix(in srgb, var(--vscode-border-primary) 50%, transparent)' }}
                >
                  <div
                    className="h-full transition-[width] duration-500"
                    style={{ width: `${Math.min(100, Math.max(0, m.composite_score))}%`, background: barColor }}
                  />
                </div>
                <div className="mt-1.5">
                  <span className={`inline-flex rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${bandBadgeCls(band)}`}>
                    {label}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          {rank != null && (
            <div
              className="absolute -top-2.5 -left-2.5 z-10 flex items-center gap-1.5 rounded-lg border px-1.5 py-1 shadow-md"
              style={{
                borderColor: 'color-mix(in srgb, var(--vscode-primary) 45%, var(--vscode-border-primary))',
                background: 'var(--vscode-bg-elevated)',
              }}
              title={`Rank ${rank}`}
            >
              <div
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 text-[11px] font-black tabular-nums"
                style={{
                  borderColor: 'var(--vscode-primary, #8b80f9)',
                  color: 'var(--vscode-primary, #8b80f9)',
                }}
              >
                {rank}
              </div>
              <span
                className="pr-0.5 text-[9px] font-bold uppercase tracking-wide"
                style={{ color: 'var(--vscode-text-primary)' }}
              >
                Rank
              </span>
            </div>
          )}

          <div
            className="ggj-match-card__hero flex items-start gap-3 rounded-t-3xl px-4 pt-4 pb-3"
            style={{
              borderBottom: '1px solid var(--vscode-border-primary)',
              background: 'color-mix(in srgb, var(--vscode-primary) 9%, var(--vscode-bg-secondary))',
            }}
          >
            <div
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl"
              style={{
                background: 'var(--vscode-primary)',
                color: 'var(--vscode-btn-primary-fg, #fff)',
                boxShadow: '0 2px 8px color-mix(in srgb, var(--vscode-primary) 35%, transparent)',
              }}
            >
              <Briefcase className="h-5 w-5" strokeWidth={2} />
            </div>
            <div className="min-w-0 flex-1">
              {variant === 'byJob' && (
                <Link
                  to={`/candidates/${m.candidate_id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="truncate text-[15px] font-semibold leading-snug"
                  style={{ color: 'var(--vscode-text-primary)' }}
                >
                  {name}
                </Link>
              )}
              {variant === 'byCandidate' && (
                <>
                  <Link
                    to={`/jobs/${m.job_id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="truncate text-[15px] font-semibold leading-snug"
                    style={{ color: 'var(--vscode-text-primary)' }}
                  >
                    {jobTitle}
                  </Link>
                  <p className="mt-0.5 flex items-center gap-1 truncate text-[10px]" style={{ color: 'var(--vscode-text-secondary)' }}>
                    <Building2 className="h-3 w-3 shrink-0" />
                    {client}
                  </p>
                </>
              )}
            </div>

            <div className="flex flex-col items-end gap-1.5 shrink-0">
              <span className={`rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${bandBadgeCls(band)}`}>
                {label}
              </span>
              {showPreserve && (
                <button
                  type="button"
                  onClick={togglePreserve}
                  disabled={preserveBusy}
                  title={preserved ? 'Preserved — re-scores will not overwrite' : 'Preserve snapshot'}
                  className={`rounded-md p-1 transition-colors ${
                    preserved
                      ? 'bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400'
                      : 'text-[color:var(--vscode-text-tertiary)] hover:text-[color:var(--vscode-text-secondary)] hover:bg-[color:var(--vscode-bg-secondary)]'
                  } disabled:opacity-50`}
                >
                  {preserveBusy ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Bookmark className={`h-3.5 w-3.5 ${preserved ? 'fill-current' : ''}`} />
                  )}
                </button>
              )}
            </div>
          </div>
        </>
      )}

      <div
        className="flex flex-1 flex-col rounded-b-3xl px-4 py-4"
        style={{ background: 'var(--vscode-bg-secondary)' }}
      >
        {variant !== 'pool' && (
          <div
            className="flex items-start gap-3 rounded-2xl border-2 px-3 py-3"
            style={{
              borderColor: 'var(--vscode-border-primary)',
              background: 'var(--vscode-bg-elevated)',
            }}
          >
            <div
              className="relative flex h-12 w-12 shrink-0 overflow-hidden rounded-2xl border-2 bg-[color:var(--vscode-bg-secondary)] text-sm font-bold"
              style={{
                borderColor: 'color-mix(in srgb, var(--vscode-primary) 35%, var(--vscode-border-primary))',
              }}
            >
              <MatchCardCandidateAvatar
                fullName={name}
                storagePath={m.candidate?.avatar_url}
                avatarFailed={avatarFailed}
                onImageError={() => setAvatarFailed(true)}
                onSignFailed={() => setAvatarFailed(true)}
                candidateId={m.candidate_id}
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="mt-0.5 flex items-center gap-2">
                <span className="text-xl font-black tabular-nums" style={{ color: 'var(--vscode-text-primary)' }}>
                  {m.composite_score}
                </span>
                <span className="text-[10px]" style={{ color: 'var(--vscode-text-tertiary)' }}>
                  /100
                </span>
                <div className="flex items-center gap-0.5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className={`h-4 w-4 sm:h-[1.125rem] sm:w-[1.125rem] shrink-0 ${i < stars ? 'fill-amber-400 text-amber-400' : 'text-slate-300 dark:text-slate-500'}`}
                      aria-hidden
                    />
                  ))}
                </div>
              </div>
              <p className={`text-[10px] font-semibold ${color}`}>{label}</p>
            </div>
            <div className="shrink-0 space-y-0.5 text-right">
              {llm != null && (
                <p className="text-[10px]" style={{ color: 'var(--vscode-text-secondary)' }}>
                  <span className="font-semibold" style={{ color: 'var(--vscode-text-primary)' }}>
                    AI
                  </span>{' '}
                  <span className="tabular-nums">{Math.round(llm)}</span>
                </p>
              )}
              {recRating != null && (
                <p className="text-[10px]" style={{ color: 'var(--vscode-text-secondary)' }}>
                  <span className="font-semibold" style={{ color: 'var(--vscode-text-primary)' }}>
                    Rec
                  </span>{' '}
                  <span className="tabular-nums">{recRating}/5</span>
                </p>
              )}
              {preserved && (
                <p className="text-[10px] font-semibold text-amber-600 dark:text-amber-400">Pinned</p>
              )}
            </div>
          </div>
        )}

        <div
          className={
            variant === 'pool'
              ? 'mt-3'
              : 'mt-3 rounded-2xl border-2 px-3 py-2.5'
          }
          style={
            variant === 'pool'
              ? undefined
              : {
                  borderColor: 'var(--vscode-border-primary)',
                  background: 'var(--vscode-bg-elevated)',
                }
          }
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <MatchCardTopReasons match={m} />
            <div className="shrink-0 sm:pt-0.5">
              <WhyMatchPopover recommendation={m.recommendation ?? ''} />
            </div>
          </div>
        </div>

        <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-medium" style={{ color: 'var(--vscode-text-primary)' }}>
          {m.candidate?.years_exp != null && (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3 shrink-0" />
              {m.candidate.years_exp} yrs
            </span>
          )}
          {variant !== 'pool' && m.candidate?.location && (
            <span className="flex items-center gap-1">
              <MapPin className="h-3 w-3 shrink-0" />
              {m.candidate.location}
            </span>
          )}
          {m.candidate?.current_company && (
            <span className="flex items-center gap-1">
              <Briefcase className="h-3 w-3 shrink-0" />
              <span className="max-w-[7rem] truncate">{m.candidate.current_company}</span>
            </span>
          )}
        </div>

        {alternateRoles.length > 0 && (
          <div
            className="mt-3 rounded-xl border px-3 py-2.5"
            style={{
              borderColor: 'var(--vscode-border-primary)',
              background: 'var(--vscode-bg-elevated)',
            }}
          >
            <p
              className="text-[11px] font-semibold uppercase tracking-wide"
              style={{ color: 'var(--vscode-text-primary)' }}
            >
              {alternateRolesHeading(m)}
            </p>
            <ul className="mt-2 space-y-1">
              {alternateRoles.map((j) => (
                <li key={j.job_id}>
                  <Link
                    to={`/role-fit/${j.candidate_id}/${j.job_id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-start justify-between gap-2 rounded-lg px-1.5 py-1 text-xs font-medium transition-colors hover:bg-[color-mix(in_srgb,var(--vscode-primary)_8%,transparent)]"
                    style={{ color: 'var(--vscode-text-primary)' }}
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-semibold leading-snug">{jobTitleFromMatch(j)}</span>
                      <span
                        className="mt-0.5 block truncate text-[10px] font-normal leading-snug"
                        style={{ color: 'var(--vscode-text-secondary)' }}
                      >
                        {clientNameFromMatch(j)}
                      </span>
                    </span>
                    <span
                      className="shrink-0 tabular-nums text-[11px] font-bold leading-none pt-0.5"
                      style={{ color: 'var(--vscode-primary, #8b80f9)' }}
                    >
                      {j.composite_score}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        {inlineToast ? (
          <p
            className={`mt-3 rounded-md border px-2 py-1.5 text-[11px] font-medium ${
              inlineToast.kind === 'ok'
                ? 'border-emerald-500/35 text-emerald-700 dark:text-emerald-300'
                : 'border-red-500/35 text-red-600 dark:text-red-400'
            }`}
            role="status"
          >
            {inlineToast.text}
          </p>
        ) : null}

        <div className="mt-auto flex flex-col gap-2 pt-3 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={handleShortlistAndIntro}
            disabled={shortlisting}
            className={`btn-primary inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold disabled:opacity-60`}
          >
            {shortlisting ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" aria-hidden /> Working…
              </>
            ) : shortlisted ? (
              <>
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0" aria-hidden /> Send intro
              </>
            ) : (
              <>
                <UserCheck className="h-3.5 w-3.5 shrink-0" aria-hidden /> Shortlist &amp; send intro
              </>
            )}
          </button>
          <Link
            to={`/role-fit/${m.candidate_id}/${m.job_id}`}
            onClick={(e) => e.stopPropagation()}
            className="btn-secondary inline-flex items-center justify-center px-3 py-2 text-xs font-semibold"
          >
            Open role fit
          </Link>
        </div>
      </div>
    </div>
  );
}
